/**
 * 화면 가이드 — 장마다 맨 앞에 붙는 "화면 둘러보기" 와 "이럴 때는 이렇게".
 *
 * 설명서의 나머지 절은 글로 된 참고서다. 처음 온 사람은 그 글보다 **실제 화면에 번호가
 * 붙은 그림**과 **상황 하나를 처음부터 끝까지 따라 하는 이야기**를 먼저 원한다. 그래서
 * 이 둘을 따로 모아 두고, 목차(`index.ts`)가 각 장의 맨 앞에 끼운다 — 장 파일마다 흩어
 * 두면 캡처 키가 바뀌었을 때 고칠 곳을 찾아 헤맨다.
 *
 * `shot` · `key` · `marker` 는 `scripts/manual/capture.mjs` 가 찍고 잰 이름이다.
 * 그림에 없는 이름을 쓰면 시험(`manual.test.ts`)이 잡는다.
 */
import type { ManualBlock, ManualSection } from '../types.js';

const tour = (blocks: readonly ManualBlock[]): ManualSection => ({ id: 'tour', title: '화면 둘러보기', blocks });
const stories = (blocks: readonly ManualBlock[]): ManualSection => ({
  id: 'scenarios',
  title: '이럴 때는 이렇게',
  blocks,
});

export interface ChapterGuide {
  tour?: ManualSection;
  scenarios?: ManualSection;
}

export const GUIDES: Readonly<Record<string, ChapterGuide>> = {
  /* ─────────────────────────── 시작하기 ─────────────────────────── */
  'getting-started': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'login',
        title: '로그인 화면',
        markers: [
          { key: 'email', label: '아이디 또는 이메일', text: '관리자에게 받은 아이디나 이메일을 넣습니다. 납품받은 서버의 첫 계정은 아이디 **admin** 입니다.' },
          { key: 'password', label: '비밀번호', text: '처음 로그인할 때는 관리자에게 받은 **임시 비밀번호**를 넣습니다. 로그인하면 새 비밀번호를 정하는 화면이 먼저 나옵니다.' },
          { key: 'submit', label: '로그인 단추', text: '누르면 대시보드가 열립니다. 10번 틀리면 15분 동안 잠깁니다.' },
          { key: 'forgot', label: '비밀번호를 잊었을 때', text: '비밀번호를 직접 찾는 기능은 없습니다. 관리자에게 재설정을 요청하세요.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'dashboard',
        title: '로그인하면 보이는 콘솔 — 어디에 무엇이 있나',
        markers: [
          { key: 'menu', label: '왼쪽 메뉴', text: '하는 일마다 메뉴가 나뉘어 있습니다. 역할에 따라 보이는 메뉴가 다릅니다.' },
          { key: 'manual', label: '사용 설명서', text: '지금 보고 있는 이 설명서입니다.' },
          { key: 'search', label: '검색', text: '콘텐츠·화면 이름을 두 글자 이상 넣으면 바로 찾습니다. 키보드 / 키를 누르면 검색칸으로 바로 이동합니다.' },
          { key: 'alerts', label: '알림 종', text: '꺼진 화면, 오류가 난 화면, 긴급 오버라이드가 있으면 빨간 숫자가 붙습니다.' },
          { key: 'account', label: '내 이름', text: '누르면 [[내 계정]](비밀번호 변경)과 [[로그아웃]] 이 나옵니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '처음 받은 계정으로 로그인하기',
        situation: '관리자에게서 아이디(또는 이메일)와 임시 비밀번호를 메신저로 받았습니다.',
        steps: [
          { text: '브라우저로 콘솔 주소를 열고 “아이디 또는 이메일” 칸에 받은 아이디(이메일)를 넣습니다.', shot: 'login', marker: 'email' },
          { text: '“비밀번호” 칸에 임시 비밀번호를 그대로 붙여 넣습니다.', shot: 'login', marker: 'password' },
          { text: '[[로그인]] 을 누르고, 이어서 나오는 화면에서 새 비밀번호(8자 이상)를 정합니다.', shot: 'login', marker: 'submit' },
          { text: '콘솔이 열리면 왼쪽 메뉴에서 할 일을 고릅니다. 필요한 설명은 아래쪽 [[사용 설명서]] 에서 찾습니다.', shot: 'dashboard', marker: 'manual' },
        ],
        result: '다음부터는 같은 아이디(이메일)와 새 비밀번호로 로그인합니다.',
      },
    ]),
  },

  /* ─────────────────────────── 대시보드 ─────────────────────────── */
  dashboard: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'dashboard',
        title: '대시보드',
        markers: [
          { key: 'stats', label: '숫자 카드 네 개', text: '켜진 화면 수, 콘텐츠 수, 편성 수, **주의가 필요한 화면 수**. 카드를 누르면 그 목록으로 갑니다.' },
          { key: 'regions', label: '위치별 화면 현황', text: '위치마다 켜진(초록)·꺼진(회색) 화면 비율. 꺼진 화면이 많은 위치가 위로 올라옵니다.' },
          { key: 'status', label: '화면 현황', text: '문제가 큰 화면부터 8대. “오류”·“미배정” 표시가 붙은 줄을 누르면 화면 상세로 갑니다.' },
          { key: 'types', label: '콘텐츠 유형', text: '이미지·영상·웹·템플릿이 몇 개인지.' },
          /*
            "최근 활동" 카드는 그림 맨 아래에 걸려 잘린다 — 번호를 달지 않고, 본문("화면 현황 · 콘텐츠
            유형 · 최근 활동" 절)이 권한 안내와 함께 설명한다(관리자·감사 역할에 보인다).
          */
          { key: 'edit', label: '편집', text: '올릴 카드를 고르고 순서·폭·높이를 바꿉니다. 내 계정에만 저장되어 다른 사람의 대시보드는 그대로입니다.' },
          { key: 'alerts', label: '알림 종', text: '어느 화면에서든 문제가 생기면 여기에 숫자가 표시됩니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '아침에 출근해서 1분 점검하기',
        situation: '밤사이 매장·지점 화면이 정상으로 재생됐는지 빠르게 확인하려 합니다.',
        steps: [
          { text: '“주의 필요” 카드의 숫자를 봅니다. **모두 정상** 이면 점검할 화면이 없습니다.', shot: 'dashboard', marker: 'stats' },
          { text: '숫자가 있으면 “화면 현황” 에서 맨 위 줄(가장 급한 화면)을 누릅니다.', shot: 'dashboard', marker: 'status' },
          { text: '화면 상세의 “편성 결정 근거” 와 “현재 화면” 사진으로 무엇이 문제인지 확인합니다.', shot: 'display-detail', marker: 'why' },
        ],
        result: '꺼진 화면은 현장에 전원·인터넷 확인을 요청하고, 편성 문제는 화면 상세에서 바로 고칩니다.',
      },
    ]),
  },

  /* ─────────────────────────── 콘텐츠 ─────────────────────────── */
  content: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'content',
        title: '콘텐츠 목록',
        markers: [
          { key: 'add', label: '콘텐츠 추가', text: '파일 업로드 · 템플릿 만들기 · 웹 URL 추가 · 빈 화면에서 만들기 중 하나를 고릅니다. [[빈 화면에서 만들기]] 는 흰 화면에 글·그림·D-day 를 넣어 새 HTML 화면을 만듭니다.' },
          { key: 'search', label: '제목 검색', text: '제목 일부만 넣어도 찾습니다.' },
          { key: 'type', label: '형식 거르기', text: '이미지·영상·템플릿·웹·HTML 중 하나만 봅니다.' },
          { key: 'filter', label: '필터', text: '태그·게시 기간·보관함으로 거릅니다. 걸려 있는 거르개 수가 단추에 붙습니다.' },
          { key: 'view', label: '보기 방식', text: '그림 카드로 보기 / 표로 보기를 바꿉니다.' },
          { key: 'card', label: '콘텐츠 카드', text: '누르면 콘텐츠 상세가 열립니다. 그림 왼쪽 아래에 종류(HTML·영상 등)가 붙습니다. 왼쪽 위 체크박스로 여러 개를 골라 한꺼번에 지울 수 있습니다.' },
          { key: 'fit', label: '화면 맞춤', text: '**HTML 카드에만** 있습니다. 카드에 마우스를 올리면 [[편집]] · [[화면 맞춤]] · [[보관]] 단추가 나오고, [[화면 맞춤]] 은 올린 HTML 이 화면에서 잘리지 않게 크기를 맞추는 창을 엽니다.' },
          { key: 'usage', label: '편성 표시', text: '이 콘텐츠가 들어 있는 플레이리스트. “편성에 없음” 이면 어느 화면에도 나가지 않습니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'content-add',
        title: '콘텐츠 추가를 누르면',
        markers: [
          { key: 'options', label: '업로드 옵션', text: '이번에 올릴 파일에 붙일 **태그**와 **게시 종료** 시각. 비워 두어도 됩니다.' },
          { key: 'upload', label: '파일 업로드', text: '컴퓨터의 이미지(JPEG·PNG·GIF·WebP)·영상(MP4·MOV·WebM 등)·**HTML(.html·.htm)** 을 올립니다. 여러 개를 한꺼번에 골라도 됩니다. HTML 을 올리면 화면 맞춤 창이 저절로 뜹니다.' },
          { key: 'template', label: '템플릿 만들기', text: '정해진 디자인에 글자만 채워 공지·홍보 화면을 만듭니다.' },
          { key: 'blank', label: '빈 화면에서 만들기', text: '흰 화면에 제목·글·그림·D-day 를 넣어 새 HTML 화면을 직접 만듭니다. 이름과 방향을 고르면 [[화면 편집]] 과 같은 편집 창이 열립니다.' },
          { key: 'web', label: '웹 URL 추가', text: '인터넷에 공개된 웹 페이지 주소를 그대로 띄웁니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'content-blank',
        title: '빈 화면에서 만들기 창',
        markers: [
          { key: 'name', label: '이름', text: '콘텐츠 목록에 보일 이름. 화면에는 나오지 않습니다.' },
          { key: 'orientation', label: '화면 방향', text: '가로 화면(1920×1080) · 세로 화면(1080×1920). 이 크기로 만들고, 크기가 다른 화면에서는 통째로 맞춰 띄웁니다.' },
          { key: 'create', label: '만들기', text: '흰 화면이 [[넣기]] 메뉴를 펼친 채로 열립니다. 저장하면 새 HTML 콘텐츠가 생깁니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'html-fit',
        title: 'HTML 화면 맞춤 창',
        markers: [
          { key: 'screen', label: '적용할 화면', text: '이 HTML 을 띄울 화면을 고르면 그 화면이 보고한 크기로 미리 봅니다. 등록된 화면 말고도 FHD·HD·세로 FHD 같은 흔한 크기를 고를 수 있습니다.' },
          { key: 'zoom', label: '크기 − +', text: '화면에서 더 작게 · 더 크게. 너무 키우면 잘립니다.' },
          { key: 'fit', label: '화면에 다 맞추기', text: '잘리지 않는 가장 큰 크기를 콘솔이 찾아 줍니다. 잘리면 먼저 이것을 누르세요.' },
          { key: 'save', label: '저장 · 되돌리기', text: '[[저장]] 해야 화면에 나갑니다. [[되돌리기]] 는 저장해 둔 크기로 돌아갑니다.' },
          { key: 'chip', label: '잘림 판정', text: '“✓ 다 보입니다” 또는 “✕ 아래 76px · 오른쪽 134px 잘림” 처럼 바로 적히고, 잘리는 가장자리에 붉은 띠가 둘립니다.' },
          { key: 'verdict', label: '판정 설명', text: '어느 화면에서 얼마나 잘리는지와 저장하지 않은 조정이 있는지를 적습니다.' },
          { key: 'advanced', label: '세부 설정', text: '기준 크기(너비·높이), 맞춤 방식(맞춤 — 다 보이게 · 채움 — 꽉 차게 · 그대로), 다른 화면에서의 미리보기. 대개 열 일이 없습니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'template-picker',
        title: '템플릿 고르기',
        markers: [
          { key: 'notice', label: '공지사항', text: '한 건을 확실히 읽혀야 할 때 — 큰 제목 + 최근 공지 목록 + 날씨.' },
          { key: 'promotion', label: '프로모션', text: '이벤트·혜택 신청을 유도할 때 — 마감 카운트다운 포함.' },
          { key: 'alert', label: '긴급알림', text: '점검·장애처럼 보는 사람이 바로 행동해야 할 때.' },
          { key: 'schedule', label: '일정/이벤트', text: '로비·회의실 앞에서 오늘 일정을 안내할 때.' },
          { key: 'dashboard', label: '대시보드', text: '실적·진행 현황을 늘 띄워 둘 때.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'template-editor',
        title: '템플릿 편집 창',
        markers: [
          { key: 'form', label: '제목·노출 시간', text: '“제목” 은 콘솔 목록에서만 쓰는 이름이고 화면에는 나오지 않습니다.' },
          { key: 'content', label: '내용 칸', text: '예시 글이 들어 있으니 고쳐 쓰면 됩니다. 칸 오른쪽 숫자는 “지금 글자 수 / 최대”.' },
          { key: 'preview', label: '미리보기', text: '입력하는 동안 실제 화면 모습이 바로 바뀝니다. 여기서 본 그대로 나갑니다.' },
          { key: 'status', label: '입력 정상', text: '빠진 필수 칸이 있으면 “오류 N곳” 으로 바뀝니다.' },
          { key: 'create', label: '만들기', text: '누르면 콘텐츠 목록에 새 카드가 생깁니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'content-detail',
        title: '콘텐츠 상세',
        markers: [
          { key: 'back', label: '목록으로', text: '콘텐츠 목록으로 돌아갑니다.' },
          { key: 'actions', label: '내용 편집 · 정보 수정 · …', text: '[[내용 편집]] 은 템플릿에만, [[화면 편집]] 은 올린 HTML 과 템플릿에만 보입니다 — 화면을 보면서 글·그림·색을 고치고 입력 칸의 값을 정합니다. 다시 변환 · 보관 · 삭제는 [[…]] 안에 있습니다. 쓰이는 중인 콘텐츠는 삭제되지 않습니다.' },
          { key: 'preview', label: '미리보기', text: '실제 화면 비율(16:9)로 보여줍니다.' },
          { key: 'info', label: '기본 정보', text: '상태 · 게시 기간 · 해상도 · 태그. ID·크기·올린 때는 [[자세히]] 에 있습니다.' },
          { key: 'deploy', label: '배포 현황', text: '이 콘텐츠가 들어 있는 플레이리스트와 그 플레이리스트를 재생하는 화면.' },
          { key: 'history', label: '최근 변경', text: '누가 언제 만들고 고쳤는지. ‘감사로그 조회’ 권한이 있는 계정(관리자·감사)에만 보입니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'screen-edit',
        title: '화면 편집 — 고른 것 고치기',
        markers: [
          { key: 'insert', label: '넣기', text: '제목·글·그림 영역·D-day·입력 칸 같은 기본 요소를 보이는 화면 가운데에 넣습니다.' },
          { key: 'undo', label: '되돌리기 · 다시 하기', text: '잘못 고쳤으면 한 단계씩 되돌립니다(Ctrl/⌘+Z).' },
          { key: 'save', label: '닫기 · 저장', text: '[[저장]] 은 고친 것이 있을 때만 눌립니다. 저장하면 이 콘텐츠를 트는 화면이 새 내용을 받습니다.' },
          { key: 'selection', label: '고른 것', text: '화면에서 누르면 파란 테두리와 동그라미가 생깁니다. 끌어 옮기고, 동그라미를 끌어 크기를 바꿉니다. 위에 뜨는 단추로 글 고치기·그림 바꾸기·복제·지우기를 합니다. 글은 한 번 더 누르면 바로 고칩니다.' },
          { key: 'from', label: '기준일', text: 'D-day 를 고르면 나옵니다. [[날짜 고르기]] 로 날을 정하거나, [[화면의 날짜 칸]] 으로 문서 안의 날짜 입력 칸에 이어 그 칸의 값으로 셉니다.' },
          { key: 'count', label: '셈 · 앞 글 · 뒤 글', text: '[[남은 날]] 은 D-12 · D-DAY · D+3, [[지난 날]] 은 기준일을 1일째로 셉니다. 앞 글·뒤 글을 붙여 “무재해 609일째” 처럼 씁니다. 편집 화면에서 바로 보이고, 화면에서는 켤 때와 자정마다 다시 셉니다.' },
          { key: 'text', label: '글자', text: '글자 크기 − +, 굵게, 정렬, 글자 색. 아래로 내리면 바탕 색·모서리·자리와 순서가 있습니다.' },
          { key: 'zoom', label: '크기 − + · 화면에 맞추기', text: '편집 화면만 확대·축소합니다. 실제 화면 크기는 바뀌지 않습니다. 빈 곳을 끌면 화면이 옮겨집니다.' },
          { key: 'hint', label: '도움말', text: '지금 할 수 있는 일을 짧게 알려 줍니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'screen-edit-insert',
        title: '화면 편집 — 넣기 메뉴',
        markers: [
          { key: 'button', label: '넣기', text: '누르면 메뉴가 펼쳐집니다. 고른 요소는 보이는 화면의 가운데에 놓이고, 글이면 바로 쓸 수 있습니다.' },
          { key: 'text', label: '글', text: '제목 · 글 · 목록 · 단추 · D-day.' },
          { key: 'dday', label: 'D-day', text: '기준일까지 남은 날이나 기준일부터 지난 날을 크게 보입니다. 넣은 뒤 오른쪽 칸에서 기준일과 셈을 정합니다.' },
          { key: 'shape', label: '그림·모양', text: '그림 영역 · 그림 · 영상 · 상자 · 구분선 · 표.' },
          { key: 'frame', label: '그림 영역', text: '그림이 들어갈 자리를 먼저 잡아 둡니다. [[그림 고르기]] 로 컴퓨터의 사진을 고르면 그 자리에 꽉 차게(또는 다 보이게) 들어갑니다.' },
          { key: 'input', label: '입력', text: '입력 칸 · 숫자 입력 · 날짜 입력 · 선택 상자 · 체크 상자. 값은 오른쪽 칸에서 정합니다.' },
          { key: 'tips', label: '사용법', text: '아무것도 고르지 않았을 때 오른쪽 칸에 나오는 짧은 사용법.' },
          { key: 'page', label: '화면 크기 · 배경 색', text: '이 문서의 편집 크기와 화면 배경 색. [[처음 모습으로 모두 되돌리기]] 는 이번에 고친 것을 모두 버립니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'screen-edit-field',
        title: '화면 편집 — 입력 칸의 값',
        markers: [
          { key: 'field', label: '입력 칸', text: '날짜·선택 상자 같은 입력 칸을 누르면 고릅니다. 편집 화면에서는 칸 안을 눌러도 값이 바뀌지 않습니다.' },
          { key: 'name', label: '칸 종류와 이름', text: '어떤 칸인지와 그 칸의 이름(옆의 글)이 나옵니다.' },
          { key: 'value', label: '값', text: '화면 기기를 아무도 만지지 않아도 화면이 뜰 때 이 값이 들어갑니다. 이 값으로 계산하는 현황판은 화면에서 계산됩니다(편집 화면에서는 계산된 숫자가 비어 보일 수 있습니다).' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '새 포스터를 올려 로비 화면에 내보내기',
        situation: '디자이너가 보내 준 “가을 신메뉴 포스터” 이미지를 오늘부터 로비 화면에 재생하려 합니다.',
        steps: [
          { text: '[[콘텐츠]] 메뉴에서 [[콘텐츠 추가]] → [[파일 업로드]] 를 누르고 이미지 파일을 고릅니다.', shot: 'content-add', marker: 'upload' },
          { text: '목록에 새 카드가 생겼는지 확인합니다. 아직 “편성에 없음” 입니다.', shot: 'content', marker: 'card' },
          { text: '[[플레이리스트]] 메뉴에서 로비 화면이 쓰는 플레이리스트를 열고, 오른쪽 [[콘텐츠]] 에서 방금 올린 포스터를 체크합니다.', shot: 'playlist-editor', marker: 'add' },
          { text: '[[저장]] 을 누릅니다.', shot: 'playlist-editor', marker: 'save' },
        ],
        result: '약 30초 뒤 로비 화면에 포스터가 순서대로 나옵니다. 콘텐츠 카드의 표시도 “편성: 1층 로비 기본” 으로 바뀝니다.',
      },
      {
        kind: 'scenario',
        title: '소방 훈련 공지를 템플릿으로 만들기',
        situation: '디자인 파일 없이 글만으로 보기 좋은 공지 화면을 만들려 합니다.',
        steps: [
          { text: '[[콘텐츠 추가]] → [[템플릿 만들기]] 를 누릅니다.', shot: 'content-add', marker: 'template' },
          { text: '[[공지사항]] 을 고릅니다.', shot: 'template-picker', marker: 'notice' },
          { text: '“제목” 에 목록에서 알아볼 이름(예: 10월 소방 훈련)을 넣습니다.', shot: 'template-editor', marker: 'form' },
          { text: '“주 내용” 의 제목·본문·날짜·작성 부서를 고쳐 씁니다. 오른쪽 미리보기가 바로 바뀝니다.', shot: 'template-editor', marker: 'content' },
          { text: '“입력 정상” 이 보이면 [[만들기]] 를 누릅니다.', shot: 'template-editor', marker: 'create' },
        ],
        result: '공지 카드가 콘텐츠 목록에 생깁니다. 플레이리스트에 담으면 화면에 나갑니다.',
      },
      {
        kind: 'scenario',
        title: '사내 현황판 HTML 을 올려 화면에 맞추기',
        situation: '전산팀이 사무실 모니터(1920×1200)에 맞춰 만든 “1공장 생산 현황판” HTML 파일을 매장 화면(1920×1080)에도 띄우려 합니다.',
        steps: [
          { text: '[[콘텐츠 추가]] → [[파일 업로드]] 를 누르고 .html 파일을 고릅니다.', shot: 'content-add', marker: 'upload' },
          { text: '올리면 화면 맞춤 창이 뜹니다. [[적용할 화면]] 에서 이 현황판을 띄울 화면을 고릅니다.', shot: 'html-fit', marker: 'screen' },
          { text: '미리보기 왼쪽 위의 판정을 봅니다. “✕ … 잘림” 이면 붉은 띠 쪽이 화면 밖으로 나갑니다.', shot: 'html-fit', marker: 'chip' },
          { text: '[[화면에 다 맞추기]] 를 누릅니다. 판정이 “✓ 다 보입니다” 로 바뀝니다.', shot: 'html-fit', marker: 'fit' },
          { text: '[[저장]] 을 누릅니다.', shot: 'html-fit', marker: 'save' },
          { text: '나중에 다시 맞출 때는 목록에서 그 카드의 [[화면 맞춤]] 을 누릅니다.', shot: 'content', marker: 'fit' },
        ],
        result: '현황판이 매장 화면 크기에 맞춰 줄어 잘리지 않고 나갑니다. 플레이리스트에 담으면 화면에 나갑니다(에이전트 0.7.17 이상, 화면 맞춤은 0.7.18 이상).',
      },
      {
        kind: 'scenario',
        title: '무재해 현황판에 시작일을 정하고 D-day 보이기',
        situation: '올려 둔 “무재해 달성 현황” HTML 의 시작일·목표배수를 정하고, 화면 한쪽에 “무재해 ○일째” 를 크게 띄우려 합니다. 화면 기기는 아무도 만지지 않으므로 값은 콘솔에서 정해 둡니다.',
        steps: [
          { text: '콘텐츠 상세에서 [[화면 편집]] 을 누릅니다.', shot: 'content-detail', marker: 'actions' },
          { text: '“무재해 시작일” 날짜 칸을 누르고, 오른쪽 칸 “값” 의 달력에서 날짜를 고릅니다. 목표배수 선택 상자도 눌러 값을 고릅니다.', shot: 'screen-edit-field', marker: 'value' },
          { text: '위의 [[넣기]] ▸ [[D-day]] 를 누릅니다. 화면 가운데에 D-day 가 들어옵니다.', shot: 'screen-edit-insert', marker: 'dday' },
          { text: '오른쪽 칸 기준일에서 [[화면의 날짜 칸]] 을 골라 “무재해 시작일” 에 잇습니다.', shot: 'screen-edit', marker: 'from' },
          { text: '셈은 [[지난 날]], 앞 글에 “무재해 ”, 뒤 글에 “일째” 를 넣습니다. 편집 화면에 바로 “무재해 609일째” 처럼 보입니다. 끌어서 자리를 잡고 글자 크기·색을 맞춥니다.', shot: 'screen-edit', marker: 'count' },
          { text: '[[저장]] 을 누릅니다.', shot: 'screen-edit', marker: 'save' },
        ],
        result: '화면 기기가 문서를 띄우면 정한 시작일·배수가 들어가 현황판이 스스로 계산하고(편집 화면에서는 계산된 숫자가 “-” 로 보입니다), D-day 는 켤 때와 자정마다 다시 셉니다.',
      },
      {
        kind: 'scenario',
        title: '빈 화면에서 로비 공지 만들기',
        situation: '디자인 파일도 템플릿도 없이, 사진 한 장과 안내 글로 1층 로비 화면에 띄울 세로 공지를 만들려 합니다.',
        steps: [
          { text: '[[콘텐츠]] 메뉴에서 [[콘텐츠 추가]] → [[빈 화면에서 만들기]] 를 누릅니다.', shot: 'content-add', marker: 'blank' },
          { text: '이름(예: 1층 로비 공지)을 적고 [[세로 화면]] 을 고른 뒤 [[만들기]] 를 누릅니다. 흰 화면이 [[넣기]] 메뉴를 펼친 채 열립니다.', shot: 'content-blank', marker: 'orientation' },
          { text: '[[제목]] 을 넣고 바로 글을 씁니다. [[그림 영역]] 을 넣어 자리와 크기를 잡은 뒤 [[그림 고르기]] 로 사진을 고릅니다.', shot: 'screen-edit-insert', marker: 'frame' },
          { text: '[[글]] 을 넣어 안내 문구를 쓰고, 오른쪽 칸에서 글자 크기·색과 화면 배경 색을 맞춥니다.', shot: 'screen-edit', marker: 'text' },
          { text: '[[새 콘텐츠로 저장]] 을 누릅니다.', shot: 'screen-edit', marker: 'save' },
        ],
        result: '새 HTML 콘텐츠가 생기고 그 상세 화면이 열립니다. 플레이리스트에 담으면 화면에 나가고, 화면 크기가 달라도 통째로 맞춰 띄웁니다. 나중에 고칠 때는 상세의 [[화면 편집]] 을 누릅니다.',
      },
    ]),
  },

  /* ─────────────────────────── 플레이리스트 ─────────────────────────── */
  playlists: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'playlists',
        title: '플레이리스트 목록',
        markers: [
          { key: 'create', label: '새 플레이리스트', text: '이름만 넣으면 빈 목록을 만들고, 이어서 편집 창이 바로 열립니다.' },
          { key: 'name', label: '이름', text: '누르면 편집 창이 열립니다.' },
          { key: 'items', label: '항목', text: '“사용 중인 항목 / 전체 항목” 수.' },
          { key: 'length', label: '총 길이', text: '한 바퀴 도는 데 걸리는 시간.' },
          { key: 'actions', label: '편집 · …', text: '복제 · 삭제는 [[…]] 안에 있습니다. 삭제는 한 번 묻고 지웁니다. 쓰는 곳이 있으면 어디서 쓰는지 목록으로 알려 주고, 다른 플레이리스트로 바꾸거나 쓰는 곳에서 빼고 지울 수 있습니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'playlist-editor',
        title: '플레이리스트 편집 창',
        markers: [
          { key: 'add', label: '콘텐츠', text: '늘 열려 있는 재료 칸입니다. 체크하면 왼쪽 목록 뒤에 바로 붙고, 체크를 풀면 빠집니다. 제목 검색과 형식 필터로 이미지·영상·웹·템플릿을 나눠 볼 수 있습니다. 칸마다 영상은 앞부분의 한 장면과 길이를, 웹은 페이지 모습과 사이트 주소를 보여 줍니다.' },
          { key: 'bulk', label: '노출 · 전환 일괄 적용', text: '노출 시간(초)과 전환 효과를 정하고 맨 오른쪽 [[전체 적용]] 을 누르면 모든 항목에 함께 들어갑니다(영상은 노출 시간에서 제외). 줄마다 다르면 칸이 “혼합” 으로 보이고, 혼합인 칸은 바꾸지 않습니다. [[효과 미리보기]] 는 효과마다 움직임을 작은 화면으로 보여 줍니다.' },
          { key: 'row', label: '항목 한 줄', text: '왼쪽 번호가 재생 차례입니다. 위에서부터 차례로 재생하고, 끝나면 처음으로 돌아갑니다. 번호 옆 ⠿ 를 끌어 순서를 바꿀 수 있습니다.' },
          { key: 'dwell', label: '노출 시간(초)', text: '이 콘텐츠를 몇 초 보여줄지.' },
          { key: 'video', label: '끝까지 재생', text: '영상 줄에는 노출 시간 칸 대신 이것이 보입니다. 영상은 끝까지 재생된 뒤 다음 항목으로 넘어가고, 제목 아래에 길이가 적힙니다. 이미지·템플릿·웹과 섞어 담을 수 있습니다.' },
          { key: 'transition', label: '전환 효과', text: '페이드 · 밀기 · 위로 밀기 · 확대 · 덮기 · 뒤집기 · 없음.' },
          { key: 'enabled', label: '사용', text: '끄면 목록에는 남기고 재생에서만 뺍니다.' },
          { key: 'order', label: '↑ ↓', text: '한 칸씩 위·아래로 옮깁니다.' },
          { key: 'remove', label: '✕', text: '이 목록에서만 뺍니다. 콘텐츠는 남습니다.' },
          { key: 'save', label: '저장', text: '바꾼 것이 있을 때만 눌립니다. 저장하면 이 목록을 재생하는 화면에 곧 반영됩니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '환영 인사를 맨 뒤로 보내고 5초로 줄이기',
        situation: '로비 화면에서 환영 인사가 너무 자주, 오래 나온다는 의견을 받았습니다.',
        steps: [
          { text: '[[플레이리스트]] 메뉴에서 “1층 로비 기본” 이름을 누릅니다.', shot: 'playlists', marker: 'name' },
          { text: '“로비 환영 인사” 줄의 [[↓]] 를 여러 번 눌러 맨 아래로 내립니다.', shot: 'playlist-editor', marker: 'order' },
          { text: '같은 줄의 노출 시간을 **5** 로 바꿉니다.', shot: 'playlist-editor', marker: 'dwell' },
          { text: '[[저장]] 을 누릅니다.', shot: 'playlist-editor', marker: 'save' },
        ],
        result: '이 목록을 재생하는 모든 화면에서 환영 인사가 마지막에 5초 동안만 나옵니다.',
      },
    ]),
  },

  /* ─────────────────────────── 화면 ─────────────────────────── */
  displays: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'displays',
        title: '화면 목록',
        markers: [
          { key: 'summary', label: '한 줄 요약', text: '전체·켜진 화면 수. 꺼진 화면·오류·배정 없음이 있으면 빨갛게 따로 셉니다.' },
          { key: 'search', label: '이름·위치 검색', text: '화면 이름이나 위치로 찾습니다.' },
          { key: 'filters', label: '상태 · 필터', text: '켜진/꺼진 화면만 고르고, 위치·숨긴 화면은 [[필터]] 에서 거릅니다. 걸린 거르개 수가 단추에 붙습니다.' },
          { key: 'add', label: '화면 추가', text: '기기 화면에 나온 6자리 코드로 새 화면을 등록합니다.' },
          { key: 'row', label: '화면 한 줄', text: '초록 점은 켜짐, 회색 점은 꺼짐. 줄을 누르면 화면 상세가 열립니다.' },
          { key: 'direct', label: '바로 보내기 표시', text: '편성 대신 임시로 보낸 콘텐츠가 나가는 중입니다.' },
          { key: 'assignment', label: '편성', text: '이 화면에 배정된 플레이리스트나 스케줄.' },
          { key: 'version', label: '버전 · 채널', text: '에이전트 버전. “canary” 는 새 버전을 먼저 받는 화면입니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'displays-bulk',
        title: '화면을 여러 대 고르면 나타나는 막대',
        markers: [
          { key: 'count', label: '고른 대수', text: '왼쪽 체크박스로 고른 화면 수.' },
          { key: 'assign', label: '편성 선택 → 일괄 배정', text: '고른 화면 모두에 같은 편성(플레이리스트·스케줄)을 배정합니다.' },
          { key: 'direct', label: '바로 보내기', text: '고른 화면 모두에 콘텐츠를 임시로 띄웁니다.' },
          { key: 'extra', label: '기타 일괄 변경', text: '둘째 줄(업데이트 채널 · 위치 · 소리 · 그룹 · 정비 PIN)을 펴고 접습니다. 처음에는 펴진 채로 열립니다.' },
          { key: 'hide', label: '숨기기', text: '목록에서만 감춥니다. 재생은 계속됩니다.' },
          { key: 'remove', label: '… (삭제)', text: '[[…]] ▸ [[삭제]]. 등록을 없앱니다. 되돌릴 수 없고 현장에서 다시 등록해야 합니다.' },
          { key: 'channel', label: '업데이트 채널', text: '고르는 즉시 적용됩니다. 새 버전을 먼저 받을 화면을 정할 때 씁니다.' },
          { key: 'location', label: '위치 적용', text: '옆에서 고른 위치로 한꺼번에 옮깁니다. 위치를 안 고르고 누르면 위치가 지워집니다.' },
          { key: 'sound', label: '소리 · 정비 PIN', text: '[[소리…]] 에서 음소거·음소거 해제를 고르면 곧바로 적용됩니다(음량은 화면 설정에서 한 대씩). [[정비 PIN…]] 은 고른 화면 모두에 같은 정비 모드 PIN 을 정합니다. 화면 그룹이 있으면 이 줄에 [[그룹 적용]] 도 섭니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'display-add',
        title: '화면 추가 창',
        markers: [
          { key: 'pending', label: '등록 대기 중인 화면', text: '에이전트를 켜서 코드를 띄우고 있는 기기가 줄마다 나옵니다 — 종류·해상도·IP 와 코드가 바뀌기까지 남은 시간. 누르면 등록 코드가 채워집니다. 목록에 없으면 아래에 코드를 직접 넣습니다.' },
          { key: 'code', label: '등록 코드', text: '기기 화면에 크게 뜬 6자리 코드를 넣습니다.' },
          { key: 'name', label: '화면 이름', text: '현장에서 알아볼 이름(예: 판교점 계산대).' },
          { key: 'location', label: '위치', text: '목록에서 고르거나 새로 적습니다. 모니터링에서 같은 위치끼리 묶입니다.' },
          { key: 'submit', label: '등록', text: '기기 화면이 “등록되었습니다” 로 바뀝니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '새로 설치한 TV 를 등록하고 매장 편성 재생하기',
        situation: '판교점 계산대 옆 TV 에 에이전트를 설치했더니 화면에 코드가 나왔습니다.',
        steps: [
          { text: 'TV 화면에 나온 6자리 코드를 확인합니다.', shot: 'player-pairing', marker: 'code' },
          { text: '콘솔 [[화면]] 메뉴에서 [[화면 추가]] 를 누릅니다.', shot: 'displays', marker: 'add' },
          { text: '“등록 코드” 에 그 코드를, “화면 이름” 에 판교점 계산대를 넣습니다.', shot: 'display-add', marker: 'code' },
          { text: '“위치” 에서 판교점을 고르고 [[등록]] 을 누릅니다.', shot: 'display-add', marker: 'submit' },
          { text: '목록에서 새 화면을 눌러 “편성 배정” 에서 매장 운영시간을 고릅니다.', shot: 'display-detail', marker: 'assign' },
        ],
        result: '약 30초 뒤 TV 에 매장 편성이 나오고, 모니터링의 판교점 묶음에 카드가 생깁니다.',
      },
      {
        kind: 'scenario',
        title: '여러 화면의 편성을 한꺼번에 바꾸기',
        situation: '강남점 화면 두 대를 모두 같은 스케줄로 바꾸려 합니다.',
        steps: [
          { text: '목록 왼쪽 체크박스로 두 화면을 고릅니다. 위에 “2대 선택됨” 막대가 나타납니다.', shot: 'displays-bulk', marker: 'count' },
          { text: '[[편성 선택…]] 에서 스케줄을 고르고 [[일괄 배정]] 을 누릅니다.', shot: 'displays-bulk', marker: 'assign' },
        ],
        result: '두 화면의 편성 칸이 새 스케줄 이름으로 바뀝니다.',
      },
    ]),
  },

  'display-detail': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'display-detail',
        title: '화면 상세',
        markers: [
          { key: 'toolbar', label: '맨 위 단추', text: '[[새로고침]] · [[변경 이력]] · 설정(톱니바퀴) · [[숨기기]] · [[…]](삭제). 바로 보내기는 오른쪽 “편성 배정” 카드에 있습니다.' },
          { key: 'now', label: '현재 화면', text: '이 페이지를 연 순간 기기가 찍어 보낸 **실제 화면 사진**. 옆의 “캡처” 표시와 시각을 확인하세요.' },
          { key: 'timeline', label: '오늘 시간표', text: '스케줄이 배정된 화면에만 나옵니다. 막대의 시각을 누르면 그때 나올 화면을 미리 보여줍니다.' },
          { key: 'why', label: '편성 결정 근거', text: '긴급 → 바로 보내기 → 화면 편성 → 캠페인 순으로 어디에서 결정됐는지 한 줄씩.' },
          { key: 'files', label: '다운로드된 콘텐츠', text: '이 기기가 재생하려고 갖고 있는 콘텐츠 — 받아 둔 파일(템플릿 로고·글꼴 포함), 그 아래 편성에 실려 오는 템플릿과 외부 주소인 웹. 템플릿에 넣은 이미지·영상·HTML 은 그 템플릿 줄 아래에 묶입니다. 편성에 있는데 못 받은 것은 표 아래에, 접어도 머리에 “못 받음 N” 이 남습니다. 깨진 파일을 골라 지우면 기기가 다시 받습니다.' },
          { key: 'info', label: '기본 정보', text: '위치·태그·그룹(여기서 고칠 수 있음), 에이전트 버전, 저장 공간. 디바이스 ID·MAC·해상도·방향은 [[기기 정보]] 에 있습니다 — MAC 은 공유기 DHCP 예약·방화벽 허용에 씁니다(안드로이드 11 이상은 OS 가 앱에 주지 않아 “보고 없음”).' },
          { key: 'assign', label: '편성 배정', text: '이 화면에서 재생할 플레이리스트·스케줄. **고르는 즉시 저장**됩니다.' },
          { key: 'settings', label: '화면 설정 (톱니바퀴)', text: '누르면 창이 열립니다 — 왼쪽 메뉴 [[소프트웨어]](버전·업데이트 채널·자동 업데이트, 맨 아래 [[고급]] 에 버전 고정) · [[화면 표시]](화면 회전) · [[전원·시작]](부팅 시 자동 실행·원격 명령 — 앱 종료·전원 종료는 [[…]] 안) · [[정비 PIN]].' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '저녁 7시에 이 화면에 무엇이 나올지 미리 보기',
        situation: '구내식당 화면에 저녁 시간대 편성이 제대로 배정됐는지 낮에 확인하려 합니다.',
        steps: [
          { text: '“오늘” 시간표 막대에서 18~22 사이(연한 색 구간)를 누르거나 시각 칸에 19:00 을 넣습니다.', shot: 'display-detail', marker: 'timeline' },
          { text: '위쪽 그림이 “선택 시각의 화면” 으로 바뀌고, 아래 목록에 그때 도는 순서가 나옵니다.', shot: 'display-detail', marker: 'now' },
          { text: '확인이 끝나면 [[지금으로]] 를 누릅니다.' },
        ],
        result: '저녁 편성(브랜드 영상)이 18:00~22:00 에 나오는 것을 미리 확인했습니다.',
      },
      {
        kind: 'scenario',
        title: '한 콘텐츠만 깨져서 나올 때',
        situation: '다른 건 잘 나오는데 “멤버십 가입 이벤트” 차례만 화면이 이상하다는 연락을 받았습니다.',
        steps: [
          { text: '“다운로드된 콘텐츠” 에서 그 파일의 체크박스를 고릅니다. 화면이 켜져 있어야 고를 수 있습니다 — 지우기는 기기가 직접 하는 일입니다.', shot: 'display-detail', marker: 'files' },
          { text: '위에 나타난 빨간 “1개 지우기” 를 누르고 확인합니다. 콘텐츠 자체는 지워지지 않습니다.' },
          { text: '약 30초 뒤 [[새로고침]] 을 누릅니다. 지금 편성에 쓰이는 파일이면 기기가 곧바로 다시 받으므로, 그 줄에 “못 받음” 이 없으면 새로 받은 것입니다.', shot: 'display-detail', marker: 'toolbar' },
        ],
        result: '내려받다 만 파일이 새 파일로 바뀌어 정상으로 재생됩니다.',
      },
    ]),
  },

  'direct-send': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'direct-send',
        title: '바로 보내기 창',
        markers: [
          { key: 'drop', label: '파일 끌어다 놓기', text: '새 사진·영상·PDF 를 이 칸에 끌어다 놓거나 [[파일 선택]] 으로 올립니다. 올린 파일은 콘텐츠 목록에도 남습니다.' },
          { key: 'library', label: '기존 콘텐츠', text: '체크하면 오른쪽 “보낼 순서” 에 붙습니다. 위의 탭으로 **사진·동영상·템플릿·웹** 을 나눠 봅니다. 영상도 다른 콘텐츠와 섞어 보낼 수 있습니다.' },
          { key: 'order', label: '보낼 순서', text: '고른 순서대로 재생합니다. × 로 뺍니다.' },
          { key: 'dwell', label: '한 장당 노출 시간', text: '사진 한 장을 몇 초씩 보여줄지. 영상은 이 값과 상관없이 끝까지 재생됩니다.' },
          { key: 'until', label: '재생 기간', text: '시간을 정하면 자동으로 원래 편성으로 돌아갑니다. “해제할 때까지” 를 고르면 사람이 해제하기 전까지 계속 나갑니다.' },
          { key: 'note', label: '메모', text: '[[메모]] 칸에 왜 보냈는지 적어 두면 다른 담당자가 이유를 알 수 있습니다.' },
          { key: 'send', label: '보내기', text: '괄호 안 숫자가 보낼 화면 수입니다. **오프라인 화면에는 보낼 수 없어** 빠지고, 뺀 화면 이름이 이 위에 적힙니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '로비 화면에 1시간만 세일 안내 띄우기',
        situation: '오후에 급하게 재고 정리 세일을 알리고, 끝나면 원래 편성으로 돌아가게 하려 합니다.',
        steps: [
          { text: '[[화면]] 메뉴에서 로비 메인 LED 를 열고, 오른쪽 “편성 배정” 카드의 [[바로 보내기]] 를 누릅니다.', shot: 'display-detail', marker: 'assign' },
          { text: '“기존 콘텐츠” 에서 주말 특가 30% 를 체크합니다.', shot: 'direct-send', marker: 'library' },
          { text: '“재생 기간” 에서 [[1시간]] 을 고릅니다.', shot: 'direct-send', marker: 'until' },
          { text: '메모에 이유를 적고 [[보내기]] 를 누릅니다.', shot: 'direct-send', marker: 'send' },
        ],
        result: '곧바로 세일 안내가 나오고, 1시간 뒤 원래 편성으로 자동으로 돌아갑니다. 먼저 끝내려면 화면 상세의 [[해제]] 를 누릅니다.',
      },
    ]),
  },

  /* ─────────────────────────── 모니터링 ─────────────────────────── */
  monitoring: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'sites',
        title: '모니터링 — 사업장 현황(그림판)',
        markers: [
          { key: 'menu', label: '조직 이름', text: '나무의 맨 위입니다. [[사업장·위치]] 메뉴에서 정한 이름이 나오고(정하지 않았으면 “전체”), 누르면 전체를 봅니다.' },
          { key: 'tree', label: '지역 › 사업장 › 화면', text: '조직 아래의 지역·사업장을 화살표로 접고 폅니다. **이름을 누르면 그림이 그 영역으로 날아가 맞춰집니다.** 줄 끝의 점은 오프라인 비율(초록·주황·빨강)이고, 숫자는 온라인/전체입니다.' },
          { key: 'crumbs', label: '고른 범위', text: '조직 › 지역 › 사업장 › 화면 순서로 지금 보는 범위를 적습니다. 누르면 그 위 범위로 돌아갑니다.' },
          { key: 'region', label: '지역 영역', text: '점선 상자 하나가 지역 하나입니다. 제목을 누르면 그 지역에 맞춰집니다.' },
          { key: 'factory', label: '사업장 영역', text: '지역 안의 실선 상자가 사업장이고, 그 안에 **위치마다 칸**이 나뉩니다. 오프라인 화면이 있으면 테두리가 호박색입니다.' },
          { key: 'node', label: '화면 노드', text: '화면 한 대가 썸네일 한 장입니다 — 화면이 보낸 마지막 그림, 없으면 편성 내용으로 그립니다. 꺼진 화면은 붉은 판이고 위치 안에서 앞에 섭니다. 누르면 그 노드에 맞추고 오른쪽에 그 화면의 판이 열려 [[화면 상세]] 로 갈 수 있습니다.' },
          { key: 'zoom', label: '확대·축소 · 배치 편집', text: '그림판의 빈 곳을 끌면 옮기고, 마우스 휠·트랙패드 핀치(휴대폰은 두 손가락)로 키우고 줄입니다. 여기 − + 로도 되고, 가운데 숫자를 누르면 100%, [[전체 보기]] 는 모든 지역이 한눈에 들어오게 맞춥니다. [[배치 편집]] 을 켜면 지역·사업장·위치 영역을 끌어 옮기고 크기를 바꿉니다(내 계정에만 저장).' },
          { key: 'config', label: '사업장 구성', text: '서버·위치마다 지역과 사업장 이름을 정합니다(이 서버만이면 [[사업장·위치]] 메뉴가 더 편합니다).' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '문제 있는 화면만 골라 보기',
        situation: '지점이 많아 전체를 확인하기 어렵습니다. 조치가 필요한 화면만 보려 합니다.',
        steps: [
          { text: '왼쪽 나무의 줄 끝 점과 **온라인/전체** 숫자로 어느 지역·사업장에 꺼진 화면이 있는지 먼저 봅니다.', shot: 'sites', marker: 'tree' },
          { text: '그 사업장 이름을 누릅니다. 그림이 그 사업장으로 맞춰지고, 꺼진 화면은 붉은 판으로 위치 칸 앞에 섭니다.', shot: 'sites', marker: 'factory' },
          { text: '붉은 노드를 눌러 오른쪽 판에서 언제부터 소식이 없는지 보고, [[화면 상세]] 로 가서 원인을 봅니다.', shot: 'sites', marker: 'node' },
          { text: '다 본 뒤 위쪽 범위 줄의 조직 이름을 누르면 전체로 돌아갑니다.', shot: 'sites', marker: 'crumbs' },
        ],
        result: '나무의 점이 모두 초록이면 모두 정상으로 돌아온 것입니다. 원격 서버까지 보려면 왼쪽 메뉴 [[통합 모니터링]] 에서 같은 순서로 봅니다.',
      },
    ]),
  },

  'integrated-monitoring': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'sites-map',
        title: '통합 모니터링 — 연동된 서버를 한 지도에',
        markers: [
          { key: 'menu', label: '통합 모니터링 메뉴', text: '다른 서버와 연동된 서버에만 생기는 메뉴입니다. 이 서버와 통합 관제에 등록한 원격 서버의 화면을 한 지도에 그립니다. 이 서버의 화면만 보는 그림판은 바로 위 [[모니터링]] 입니다.' },
          { key: 'tree', label: '원격 서버까지 한 나무', text: '원격 서버의 지역·사업장도 함께 섭니다. 줄 끝의 점이 회색이면 연결이 끊긴 서버입니다.' },
          { key: 'place', label: '지도 위 노드 카드', text: '화면이 놓인 위치의 주소마다 카드 하나 — 아래 꼬리가 그 자리를 가리키고, 안에 위치마다 화면이 섭니다. 테두리 색은 오프라인 비율입니다. 지도와 함께 커지고 작아지며, 멀리 보면 화면 수가 든 점으로 접힙니다. 점을 누르면 그 카드가 활성 — 확대·축소해도 제 크기 그대로입니다(지도 빈 곳을 누르면 풀립니다). 자리는 위치마다 정한 주소입니다([[주소 정하기]]).' },
          { key: 'panel', label: '사업장의 판', text: '고른 사업장의 주소와 숫자, 위치마다 주소와 화면입니다. 위치 주소 옆 [[주소 정하기]] 로 그 위치의 주소를 곧바로 저장하면 지도에 노드 카드가 섭니다.' },
          { key: 'node', label: '원격 서버의 화면', text: '원격 서버의 화면은 썸네일 없이 상태(온라인·오프라인)만 그립니다 — 그 서버의 그림은 망이 달라 이 브라우저가 닿지 않을 수 있습니다. 누르면 화면의 판이 열려 [[통합 관제에서 열기]] 로 갈 수 있습니다.' },
          { key: 'zoom', label: '확대·축소 · 전체 보기', text: '지도의 빈 곳을 끌면 옮기고, 마우스 휠·트랙패드 핀치(휴대폰은 두 손가락)로 키우고 줄입니다. 여기 − + 로도 되고, [[전체 보기]] 는 모든 카드가 한눈에 들어오게 맞춥니다.' },
        ],
      },
    ]),
  },

  hub: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'hub',
        title: '통합 관제 — 서버 셋을 묶고 창원 공장을 연 모습',
        markers: [
          { key: 'stats', label: '합계', text: '등록한 서버 수와, 그 서버들의 화면 수·온라인·주의·정비 중을 더한 것입니다. 연결이 끊긴 서버는 “서버 · 연결 끊김 n” 으로 따로 세고 화면 숫자에서는 뺍니다(“끊긴 서버 제외”).' },
          { key: 'add', label: '서버 추가', text: '다른 서버의 이름·주소·토큰을 넣습니다. 저장하기 전에 그 서버에 닿는지·토큰이 통하는지 확인합니다. 환경 설정 권한이 있는 계정(관리자)에게만 보입니다.' },
          { key: 'lost', label: '연결 끊김', text: '이 서버가 그 서버에 닿지 못합니다(망·그리드가 끊겼거나 그 서버가 꺼짐). **화면 장애가 아닙니다** — 마지막으로 받은 숫자를 흐리게 남기고, 줄 아래에 까닭을 한 줄 적습니다(마우스를 올리면 전체).' },
          { key: 'actions', label: '지금 확인 · 고치기 · 지우기', text: '[[지금 확인]] 은 30초를 기다리지 않고 지금 묻습니다(토큰까지 다시 확인). 연필은 이름·주소·토큰 [[고치기]](서버 이름을 눌러도 됩니다), 휴지통은 통합 관제에서 빼기 — 그 서버와 화면에는 아무 일도 없습니다.' },
          { key: 'displays', label: '그 서버의 화면', text: '서버 줄을 누르면 아래에 그 서버의 화면 목록이 열립니다 — 그 서버에서 지금 받아 온 것입니다. 다시 누르면 닫힙니다. 목록 위에는 오프라인 화면이 있는 위치를 적습니다.' },
          { key: 'filter', label: '찾기 · 거르기', text: '이름·위치로 찾고, 전체·주의·온라인·오프라인으로 거릅니다. 200대가 넘으면 목록 아래 [[더 보기]].' },
          { key: 'command', label: '원격 명령', text: '둥근 화살표가 [[기기 재부팅]], [⋯] 안에 [[앱 종료]] · [[전원 종료]](정비 중인 화면이면 [[정비 모드 해제]] 도). 보낼 수 없으면 단추가 꺼져 있고 마우스를 올리면 까닭이 보입니다(오프라인·옛 재생 프로그램·권한 없음).' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '현장 서버 하나를 본사 통합 관제에 묶기',
        situation: '창원 공장에 서버를 따로 두었습니다. 본사 서버에서 창원 공장의 화면도 함께 보고 싶습니다.',
        steps: [
          { text: '창원 공장 서버의 콘솔에 관리자로 로그인해 [[환경 설정]] ▸ “통합 관제 연결” 에서 [[통합 관제 토큰 만들기]] 를 누르고, 나온 토큰을 복사합니다. 패널 아래에 그 서버의 주소가 적혀 있습니다.', shot: 'settings', marker: 'hublink' },
          { text: '본사 서버의 [[통합 관제]] 에서 [[서버 추가]] 를 누르고, 이름(창원 공장)·창원 서버의 주소·복사한 토큰을 넣어 [[저장]] 합니다. 망이 다르면 주소는 Sentinel Grid 주소입니다.', shot: 'hub', marker: 'add' },
          { text: '목록에 창원 공장이 “연결됨” 으로 나오고 화면 숫자가 채워지면 끝입니다. 줄을 눌러 화면 목록이 받아지는지도 봅니다.', shot: 'hub', marker: 'displays' },
        ],
        result: '본사의 [[통합 관제]] 와 [[통합 모니터링]] 에 창원 공장 화면이 함께 나옵니다. 지도에 세우려면 [[통합 모니터링]] 의 [[사업장 구성]] 에서 창원 공장의 지역·사업장·주소를 정합니다.',
      },
      {
        kind: 'scenario',
        title: '서버가 “연결 끊김” 일 때',
        situation: '아침에 보니 평택 물류센터가 “연결 끊김” 이고 숫자가 흐립니다.',
        steps: [
          { text: '줄 아래의 까닭을 읽습니다. “5초 안에 답이 없습니다”·“그 주소로 가는 길이 없습니다” 는 두 서버 사이의 망이 끊긴 것이고, “연결을 거부했습니다” 는 그 서버에는 닿았지만 SignageOne 이 그 포트에서 받지 않는 것(서비스가 멈췄거나 포트가 다름)입니다.', shot: 'hub', marker: 'lost' },
          { text: '평택 서버의 콘솔을 직접 열어 봅니다. 열리면 서버는 살아 있고 두 서버 사이의 길(사내망·Sentinel Grid)이 끊긴 것이니 망 담당이나 공급사에 알립니다.' },
          { text: '고쳤다는 연락을 받으면 [[지금 확인]] 을 누릅니다.', shot: 'hub', marker: 'actions' },
        ],
        result: '“연결됨” 으로 돌아오면 숫자가 다시 진해지고 합계에 들어갑니다. 끊긴 동안에도 평택의 화면은 평택 서버의 편성대로 재생했습니다 — 끊김은 화면 장애가 아닙니다.',
      },
    ]),
  },

  /* ─────────────────────────── 스케줄 · 캠페인 ─────────────────────────── */
  schedules: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'schedules',
        title: '스케줄 목록',
        markers: [
          { key: 'create', label: '새 스케줄', text: '편성표를 새로 만듭니다.' },
          { key: 'card', label: '스케줄 카드', text: '이름 옆에 시각 기준·이 스케줄을 쓰는 화면 수·규칙 수가 보입니다. 이름을 누르면 편집 창이 열립니다.' },
          { key: 'toggle', label: '펼치기', text: '카드는 접힌 채로 열립니다. 이름 왼쪽 화살표를 누르면 평소 편성과 규칙이 펼쳐집니다.' },
          { key: 'default', label: '평소 편성', text: '규칙에 걸리지 않는 시간에 재생할 플레이리스트나 콘텐츠 하나. 규칙이 없으면 하루 종일 이것이 나갑니다.' },
          { key: 'rule', label: '시간대 규칙', text: '요일 · 시간 · 재생할 플레이리스트 · 우선순위. “(익일)” 은 다음 날까지라는 뜻입니다.' },
          { key: 'actions', label: '편집 · …', text: '복제 · 삭제는 [[…]] 안에 있습니다. 쓰이는 스케줄은 삭제가 막힙니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'schedule-editor',
        title: '스케줄 편집 창',
        markers: [
          { key: 'name', label: '이름', text: '예: 매장 운영시간.' },
          { key: 'default', label: '평소 편성', text: '규칙에 걸리지 않는 모든 시간에 나가는 플레이리스트(또는 콘텐츠 하나).' },
          { key: 'day', label: '하루 미리보기', text: '오른쪽 열. 요일을 누르면 그날 하루가 어떻게 나뉘는지 막대로 보여줍니다. 구간을 누르면 그 구간을 만든 규칙으로 이동합니다. 저장 전에 반드시 확인하세요.' },
          { key: 'rules', label: '시간대 규칙', text: '요일 단추, 시작~끝 시각, “에는” 뒤에 재생할 플레이리스트. 하루 종일이면 [[종일]].' },
          { key: 'add', label: '규칙 추가', text: '평일 09:00~18:00 규칙이 하나 생깁니다. 이 규칙을 고쳐서 사용합니다.' },
          { key: 'save', label: '저장', text: '시각이 비어 있으면 눌리지 않습니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '평일 점심시간(11:30~13:00)에만 메뉴 재생하기',
        situation: '구내식당 화면에 평소에는 매장 기본 편성을, 평일 점심에는 점심 메뉴를 재생하려 합니다.',
        steps: [
          { text: '[[스케줄]] 메뉴에서 [[새 스케줄]] 을 누릅니다.', shot: 'schedules', marker: 'create' },
          { text: '이름을 넣고 “평소 편성” 에서 매장 기본 편성을 고릅니다.', shot: 'schedule-editor', marker: 'default' },
          { text: '[[규칙 추가]] 를 누르고, [[평일]] · 11:30 ~ 13:00 · “에는” 점심 메뉴로 맞춥니다.', shot: 'schedule-editor', marker: 'rules' },
          { text: '오른쪽 “하루 미리보기” 에서 월요일을 눌러 11:30~13:00 만 색이 다른지 봅니다.', shot: 'schedule-editor', marker: 'day' },
          { text: '[[저장]] 한 뒤 화면 상세의 “편성 배정” 에서 이 스케줄을 고릅니다.', shot: 'display-detail', marker: 'assign' },
        ],
        result: '평일 점심에만 메뉴가 나오고, 나머지 시간과 주말에는 평소 편성이 나옵니다.',
      },
    ]),
  },

  campaigns: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'campaigns',
        title: '캠페인 목록',
        markers: [
          { key: 'create', label: '새 캠페인', text: '기간·대상을 정해 평소 편성 위에 얹습니다.' },
          { key: 'emergency', label: '긴급', text: '모든 편성보다 먼저 나가는 [[긴급 송출]] 과, 그 옆 [[…]] 안의 [[전 화면 비우기]](잘못 나간 것을 당장 걷기).' },
          { key: 'status', label: '상태', text: '게재 중(초록) · 예정(파랑) · 종료 · 중지됨.' },
          { key: 'mode', label: '방식', text: '“전체 대체” 는 캠페인만 재생하고, “N개마다 삽입” 은 평소 콘텐츠 사이에 끼워 넣습니다.' },
          { key: 'target', label: '대상', text: '전체 화면 · 태그가 붙은 화면 · 그룹. 다른 담당의 화면에 걸린 캠페인은 “범위 밖” 으로 보입니다.' },
          { key: 'period', label: '기간', text: '시작 ~ 끝 날짜. 게재 시간대를 정했으면 함께 적힙니다.' },
          { key: 'actions', label: '편집 · 중지 · …', text: '[[중지]] 는 지우지 않고 잠시 끕니다. 복제 · 삭제는 [[…]] 안에 있습니다.' },
        ],
      },
      {
        kind: 'screen',
        shot: 'campaign-editor',
        title: '캠페인 편집 창',
        markers: [
          { key: 'playlist', label: '플레이리스트', text: '캠페인 기간에 재생할 플레이리스트.' },
          { key: 'mode', label: '재생 방식', text: '전체 대체 / 삽입(몇 개마다 끼울지 함께 정함).' },
          { key: 'target', label: '대상', text: '전체 화면 · 태그로 지정 · 그룹으로 지정.' },
          { key: 'period', label: '시작 · 종료', text: '비워 두면 그쪽은 기한이 없습니다.' },
          { key: 'dayparts', label: '게재 시간대', text: '정하지 않으면 기간 안에서 하루 종일 나갑니다. [[시간대 추가]] 로 요일·시각을 제한합니다.' },
          { key: 'priority', label: '우선순위', text: '[[고급 설정]] 안에 있습니다. 캠페인끼리 겹치면 숫자가 큰 캠페인이 우선합니다.' },
          { key: 'save', label: '저장', text: '새로 만들 때는 [[만들기]] 로 보입니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '한 달 동안 매장 화면에 신메뉴 광고 끼워 넣기',
        situation: '평소 편성은 그대로 두고, 매장 화면에 신메뉴 광고를 콘텐츠 3개마다 한 번씩 끼워 넣으려 합니다.',
        steps: [
          { text: '[[캠페인]] 메뉴에서 [[새 캠페인]] 을 누르고 이름과 광고 플레이리스트를 고릅니다.', shot: 'campaign-editor', marker: 'playlist' },
          { text: '“재생 방식” 에서 삽입을 고르고 “삽입 간격” 에 3 을 넣습니다.', shot: 'campaign-editor', marker: 'mode' },
          { text: '“대상” 에서 태그로 지정을 고르고 매장 을 넣습니다.', shot: 'campaign-editor', marker: 'target' },
          { text: '시작·종료 날짜를 한 달로 정합니다.', shot: 'campaign-editor', marker: 'period' },
          { text: '[[만들기]] 를 누르고 목록의 상태를 확인합니다.', shot: 'campaigns', marker: 'status' },
        ],
        result: '기간 동안 매장 화면에 광고가 섞여 나오고, 끝나는 날이 지나면 자동으로 멈춥니다.',
      },
    ]),
  },

  'what-plays': {
    scenarios: stories([
      {
        kind: 'scenario',
        title: '의도한 편성이 나오지 않는 원인 찾기',
        situation: '스케줄을 바꿨는데 강남점 입구 화면에는 여전히 다른 콘텐츠가 나온다는 연락을 받았습니다.',
        steps: [
          { text: '[[화면]] 목록에서 그 화면을 찾아 누릅니다.', shot: 'displays', marker: 'row' },
          { text: '화면 상세의 “편성 결정 근거” 를 봅니다. “캠페인”, “바로 보내기” 처럼 위에서부터 어느 단계에서 결정됐는지 나옵니다.', shot: 'display-detail', marker: 'why' },
          { text: '캠페인이 원인이면 [[캠페인]] 메뉴에서 그 캠페인의 기간·대상을 고치거나 [[중지]] 합니다.', shot: 'campaigns', marker: 'actions' },
        ],
        result: '우선순위가 높은 편성을 해제하면 약 30초 뒤 스케줄 편성이 나옵니다.',
      },
    ]),
  },

  /* ─────────────────────────── 기록 ─────────────────────────── */
  reports: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'reports',
        title: '리포트',
        markers: [
          { key: 'period', label: '기간', text: '시작·끝 날짜. 고르는 즉시 다시 집계합니다.' },
          { key: 'group', label: '집계 기준', text: '콘텐츠별 · 캠페인별 · 위치별 · 화면별 · 플레이리스트별 · 일자별.' },
          { key: 'print', label: '인쇄용 보고서', text: 'A4 한 장짜리 보고서. 인쇄 창에서 PDF 로 저장합니다.' },
          { key: 'xlsx', label: '엑셀 보고서', text: '요약과 축마다 시트 한 장 — 윗선·광고주 보고용.' },
          { key: 'csv', label: 'CSV 원자료', text: '재생 한 번이 한 줄인 원자료. 기간 전체를 빠짐없이 담습니다.' },
          { key: 'totals', label: '합계', text: '총 재생 횟수 · 총 노출 시간 · 재생한 화면 수. 표가 잘려도 기간 전체입니다.' },
          { key: 'table', label: '집계표', text: '많이 나간 순. 막대는 1위와 비교한 비율입니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '지난달 캠페인 결과를 엑셀 보고서로 올리기',
        situation: '홍보팀장에게 지난달 캠페인이 몇 번, 몇 대의 화면에서 나갔는지 보고해야 합니다.',
        steps: [
          { text: '기간을 지난달 1일 ~ 말일로 고릅니다.', shot: 'reports', marker: 'period' },
          { text: '집계 기준을 캠페인별로 바꿔 숫자를 확인합니다.', shot: 'reports', marker: 'group' },
          { text: '[[엑셀 보고서]] 를 눌러 파일을 받습니다.', shot: 'reports', marker: 'xlsx' },
        ],
        result: '요약·콘텐츠별·캠페인별·위치별·화면별·플레이리스트별·일자별 시트가 든 엑셀을 받습니다. 그대로 첨부하거나 필요한 시트만 골라 보고서에 붙입니다.',
      },
      {
        kind: 'scenario',
        title: '회의 자료로 한 장짜리 PDF 만들기',
        situation: '주간 회의에 화면 운영 실적을 한 장으로 가져가야 합니다.',
        steps: [
          { text: '기간을 지난주로 고르고 [[인쇄용 보고서]] 를 누릅니다.', shot: 'reports', marker: 'print' },
          { text: '[[인쇄 · PDF 저장]] 을 누르고, 인쇄 창의 대상을 “PDF로 저장” 으로 바꿉니다.' },
        ],
        result: '회사 로고·이름이 머리에 들어간 A4 한 장(합계·콘텐츠별·캠페인별·위치별·일자별)이 PDF 로 저장됩니다.',
      },
    ]),
  },

  /* ─────────────────────────── 에이전트 ─────────────────────────── */
  'agent-install': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'player-server',
        title: '기기 화면 — 서버 주소를 물을 때',
        markers: [
          { key: 'url', label: '서버 주소', text: '보통은 설치할 때 채워집니다. 비어 있으면 공급사(설치 담당자)에게 받은 주소를 넣습니다.' },
          { key: 'connect', label: '적용', text: '누르면 그 주소가 쓸 수 있는 서버인지 먼저 확인합니다. 닿지 않거나 SignageOne 서버가 아니면 이 화면에 머무르며 이유를 알려 주므로, 주소를 고쳐 다시 누르면 됩니다. 확인되면 등록 코드 화면으로 넘어갑니다(이미 등록된 화면이면 바로 재생으로).' },
        ],
      },
      {
        kind: 'screen',
        shot: 'player-pairing',
        title: '기기 화면 — 등록 코드',
        markers: [
          { key: 'code', label: '6자 페어링 코드', text: '콘솔 [[화면 추가]] 에 이 코드를 넣습니다. 15분마다 새로 바뀝니다.' },
          { key: 'hint', label: '안내 문구', text: '등록되면 “등록되었습니다” 로 바뀌고 편성을 받아 옵니다.' },
        ],
      },
    ]),
  },

  'agent-device': {
    tour: tour([
      {
        kind: 'screen',
        shot: 'player-maintenance',
        title: '기기 화면 — 정비 모드',
        markers: [
          { key: 'title', label: '정비 모드', text: '여는 동안 재생과 재생 기록이 멈춥니다. 왼쪽 메뉴에서 보고 싶은 항목을 고르면 오른쪽에 그 내용이 나옵니다.' },
          { key: 'screen', label: '개요', text: '이 기기가 콘솔의 어느 화면인지(이름 · 디바이스 ID)와 메뉴마다의 상태 요약(재생 · 서버 연결 · 네트워크 · 소프트웨어 판 · 자동 실행), 시스템 정보입니다. 요약 칸을 누르면 그 메뉴로 갑니다. 여는 순간 이 항목이 열려 있습니다.' },
          { key: 'server', label: '서버 연결', text: '이 화면이 어느 서버에 보고하는지. [[적용]] 은 그 서버가 쓸 수 있는 곳인지 먼저 확인한 뒤 바꾸며, 이미 등록된 서버면 등록을 그대로 두고 주소만 옮깁니다.' },
          { key: 'network', label: '네트워크', text: '지금 붙어 있는 방식과 IP · 게이트웨이 · DNS 입니다. 바꾸려면 [[네트워크 설정 열기]] 로 기기의 설정 화면을 엽니다 — 에이전트가 직접 바꾸지는 못합니다.' },
          { key: 'update', label: '소프트웨어', text: '새 버전이 있는지 확인하고([[지금 확인]]) 바로 설치합니다([[지금 업데이트]]). [[자동 업데이트]] 도 이 안에 있으며, 특별한 이유가 없으면 켜 두세요. 웹 브라우저로 재생하는 화면에서는 눌리지 않습니다.' },
          { key: 'autostart', label: '전원·시작', text: '기기를 켜면 저절로 실행할지(기본 켜짐 — 꺼 두면 정전 뒤에 사람이 가서 켜야 합니다)와, [[앱 종료]] · [[기기 재부팅]] · [[전원 종료]] 단추입니다 — 앱 종료는 여기에만 있습니다. 모두 한 번 더 묻습니다. **[[앱 종료]] 는 닫기가 아닙니다** — 에이전트가 완전히 꺼져 화면에 아무것도 나오지 않습니다. 안드로이드는 기기가 허락할 때만 재부팅 · 전원 종료가 됩니다.' },
          { key: 'close', label: '닫기 (×)', text: '**작업이 끝나면 반드시** 누릅니다. 정비 모드를 닫고 재생을 다시 시작합니다 — 원격으로는 닫을 수 없습니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '현장에서 업데이트를 직접 받고 재생으로 돌아가기',
        situation: '새 버전이 나왔는데 이 화면만 한참째 옛 버전입니다.',
        steps: [
          { text: '화면 아무 곳이나 빠르게 두 번 누르고(Windows 는 더블클릭 또는 F10) PIN 을 넣어 정비 모드를 엽니다.', shot: 'player-maintenance', marker: 'title' },
          { text: '왼쪽 메뉴에서 [[소프트웨어]] 를 누릅니다.', shot: 'player-maintenance', marker: 'update' },
          { text: '[[지금 확인]] 을 누르고 새 버전이 보이면 [[지금 업데이트]] 를 누릅니다. 설치 확인 창이 뜨면 “설치” 를 누릅니다. 같은 화면의 [[자동 업데이트]] 가 켜져 있는지도 함께 봅니다.', shot: 'player-maintenance', marker: 'update' },
          { text: '마지막으로 오른쪽 위 **×** 를 눌러 재생으로 돌아갑니다.', shot: 'player-maintenance', marker: 'close' },
        ],
        result: '화면이 새 버전으로 다시 시작해 편성을 재생합니다. 콘솔 화면 목록의 버전 칸도 바뀝니다.',
      },
    ]),
  },

  'agent-update': {
    scenarios: stories([
      {
        kind: 'scenario',
        title: '새 버전을 두 대에서 먼저 받아 보기',
        situation: '공급사가 새 에이전트 버전을 내보내기로 했습니다. 전체로 나가기 전에 몇 대에서 먼저 확인하려 합니다.',
        steps: [
          { text: '[[화면]] 목록에서 시험할 두 화면을 고르고 [[기타 일괄 변경]] ▸ [[업데이트 채널…]] 에서 카나리를 고릅니다.', shot: 'displays-bulk', marker: 'channel' },
          { text: '공급사에 새 버전을 카나리로 먼저 내보내 달라고 알립니다.' },
          { text: '하루 정도 뒤 화면 목록의 버전 칸이 새 버전인지, 재생이 정상인지 확인합니다.', shot: 'displays', marker: 'version' },
          { text: '문제가 없으면 공급사에 안정(전체)으로 내보내도 된다고 알립니다.' },
        ],
        result: '안정으로 나가면 켜져 있는 화면들이 1~2분 안에 차례로 새 버전을 받습니다.',
      },
    ]),
  },

  /* ─────────────────────────── 관리 ─────────────────────────── */
  users: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'users',
        title: '사용자',
        markers: [
          { key: 'add', label: '사용자 추가', text: '이름·이메일·역할을 넣으면 임시 비밀번호가 한 번 보입니다.' },
          { key: 'role', label: '역할', text: '고르는 즉시 저장됩니다. 바뀐 사람은 다시 로그인해야 합니다.' },
          { key: 'scope', label: '담당 범위', text: '“전체” 또는 맡은 화면 그룹. [[바꾸기]] 로 사옥·지점 담당자에게 자기 화면만 맡깁니다.' },
          { key: 'status', label: '상태', text: '사용 중 · 임시 비밀번호(아직 첫 로그인 전) · 잠김 · 비활성.' },
          { key: 'actions', label: '… (계정 작업)', text: '비밀번호 재설정 · 비활성화 · 삭제가 들어 있습니다. 잠긴 계정은 [[비밀번호 재설정]] 으로 풉니다. 삭제는 되돌릴 수 없습니다.' },
          { key: 'roles', label: '역할이 할 수 있는 일', text: '역할마다 할 수 있는 일을 한 줄로 봅니다. 머리를 누르면 접힙니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '새로 온 매장 담당자 계정 만들기',
        situation: '콘텐츠와 편성을 맡을 직원이 새로 왔습니다.',
        steps: [
          { text: '[[사용자 추가]] 를 누르고 이름·이메일을 넣습니다. 역할은 운영을 고릅니다.', shot: 'users', marker: 'add' },
          { text: '나타난 임시 비밀번호를 [[복사]] 해 메신저로 전하고 [[전달 완료]] 를 누릅니다.' },
          { text: '목록의 상태가 “임시 비밀번호” 이면 아직 첫 로그인 전입니다. 로그인해서 비밀번호를 바꾸면 “사용 중” 이 됩니다.', shot: 'users', marker: 'status' },
        ],
        result: '새 담당자가 운영 역할로 콘솔을 사용할 수 있습니다.',
      },
    ]),
  },

  settings: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'settings',
        title: '환경 설정',
        markers: [
          { key: 'brand', label: '브랜드', text: '조직 이름 · 주 색 · 로고 · 사내 글꼴. 오른쪽 미리보기가 저장 전의 모습입니다.' },
          { key: 'quiet', label: '대기 화면 · 운영 시간', text: '편성이 비었을 때의 화면과, 화면이 쉬는 시간.' },
          { key: 'allow', label: '웹 콘텐츠 허용 주소', text: '눌러 펼칩니다(머리에 적어 둔 주소 수). 사내 포털처럼 사내망 페이지를 화면에 띄울 때 그 주소를 한 줄에 하나씩.' },
          { key: 'hublink', label: '통합 관제 연결', text: '이 서버를 다른 서버의 통합 관제에 묶을 때 그쪽에 넣을 토큰을 [[통합 관제 토큰 만들기]] 로 만듭니다(한 번만 보입니다). 토큰 줄의 **마지막 사용**이 몇 분 안쪽이면 묶여 있는 것입니다 — 통합 관제 장.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '밤에는 화면을 끄기',
        situation: '영업이 끝난 밤에도 로비 화면이 광고를 틀고 있습니다.',
        steps: [
          { text: '“대기 화면 · 운영 시간” 에서 “운영 시간 정하기” 를 켭니다.', shot: 'settings', marker: 'quiet' },
          { text: '요일을 모두 고르고 여는 시각 07:00, 닫는 시각 22:00 을 넣은 뒤 [[저장]] 을 누릅니다.', shot: 'settings', marker: 'quiet' },
        ],
        result: '22시가 되면 화면이 검게 쉬고 7시에 다시 켜집니다. 24시간 로비 화면은 화면 상세의 [[화면 설정]] 에서 “늘 켜기” 로 둡니다.',
      },
      {
        kind: 'scenario',
        title: '템플릿에 회사 로고와 색 넣기',
        situation: '공지 템플릿의 머리글을 회사 것으로 맞추고 싶습니다.',
        steps: [
          { text: '조직 이름을 넣고 주 색을 고릅니다.', shot: 'settings', marker: 'brand' },
          { text: '로고 목록에서 회사 로고 이미지를 고르고 [[저장]] 을 누릅니다.', shot: 'settings', marker: 'brand' },
        ],
        result: '약 30초 뒤 모든 템플릿 화면의 머리글에 회사 이름·로고와 색이 들어갑니다.',
      },
    ]),
  },

  account: {
    tour: tour([
      {
        kind: 'screen',
        shot: 'account',
        title: '내 계정',
        markers: [
          { key: 'profile', label: '계정', text: '이름·이메일·역할.' },
          { key: 'password', label: '비밀번호 변경', text: '바꾸면 이 브라우저만 남기고 다른 기기의 로그인이 모두 끊깁니다.' },
          { key: 'devices', label: '로그인된 기기', text: '내 계정으로 로그인해 있는 브라우저 목록. “이 기기” 가 지금 보고 있는 곳입니다.' },
          { key: 'logoutOthers', label: '다른 기기 모두 로그아웃', text: '공용 PC 에서 로그아웃을 잊었을 때 누릅니다.' },
        ],
      },
    ]),
    scenarios: stories([
      {
        kind: 'scenario',
        title: '매장 공용 PC 에서 로그아웃을 잊었을 때',
        situation: '어제 매장 PC 로 콘솔에 로그인한 뒤 로그아웃하지 않고 그대로 두고 왔습니다.',
        steps: [
          { text: '이름 동그라미 → [[내 계정]] 을 열고 “로그인된 기기” 에서 모르는 기기가 있는지 봅니다.', shot: 'account', marker: 'devices' },
          { text: '[[다른 기기 모두 로그아웃]] 을 누르고 확인합니다.', shot: 'account', marker: 'logoutOthers' },
          { text: '필요하면 비밀번호도 함께 바꿉니다.', shot: 'account', marker: 'password' },
        ],
        result: '지금 사용 중인 브라우저만 남고 매장 PC 의 로그인은 끊깁니다.',
      },
    ]),
  },
};
